import type { CustomCellEditorProps } from '../react.js';
import { useMemo, useState } from 'react';

import { type CurrencyCodeType } from '@mint-lib/currencies-helpers';
import {
  useFormattedCurrencyInput,
  useFormattedNumberInput,
  useFormattedPercentInput,
} from '@mint-lib/formatting-hooks';
import { useTranslation } from '@mint-lib/i18n';
import {
  Input,
  TruncateText,
  typographyVariants,
} from '@mint-lib/tw-components';
import {
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxMenu,
} from '@mint-lib/tw-components/patterns';

type GoalValueType = 'currency' | 'percent' | 'decimal';

type GoalOption = {
  value: string;
  label: string;
  valueType: GoalValueType;
  decimalPlaces: number | null;
};

type GoalOptionGroup = {
  key: string;
  label: string;
  options: GoalOption[];
};

export type GoalCellValue = {
  id: string;
  value: number | null;
};

export interface GoalEditorProps extends CustomCellEditorProps<
  unknown,
  GoalCellValue | null
> {
  /**
   * Metrics to choose from, pre-grouped. Each group renders as a labelled
   * section in the dropdown. Omit for an editor with no options.
   */
  groups?: GoalOptionGroup[];
  /**
   * Currency used to format the value input for `currency` metrics. Required
   * whenever `groups` contains one: without it, selecting such a metric renders
   * no value input at all.
   */
  currency?: CurrencyCodeType;
  /** Heading above the goal selector. Defaults to a generic label. */
  title?: string;
  /**
   * Note rendered under the value input. Omit to render nothing — the editor
   * carries no copy of its own beyond the generic defaults.
   */
  hint?: string;
}

type ValueInputProps = {
  value: number | null;
  maxDecimals?: number;
  onChange: (next: number | null) => void;
};

const NumberGoalInput = ({ value, maxDecimals, onChange }: ValueInputProps) => {
  const inputProps = useFormattedNumberInput({
    value,
    onChange: (next) => onChange(next),
    maxDecimals,
    autoFocus: true,
    size: 'small',
    restFormatProps: { min: 0 },
  });

  return (
    <Input
      className="w-full"
      aria-label="goal-value-number"
      data-dd-action-name="Grid cell goal: edit number value"
      {...inputProps}
    />
  );
};

const PercentGoalInput = ({
  value,
  maxDecimals,
  onChange,
}: ValueInputProps) => {
  const inputProps = useFormattedPercentInput({
    value,
    onChange: (next) => onChange(next),
    maxDecimals,
    autoFocus: true,
    size: 'small',
  });

  return (
    <Input
      className="w-full"
      aria-label="goal-value-percent"
      data-dd-action-name="Grid cell goal: edit percent value"
      {...inputProps}
    />
  );
};

const CurrencyGoalInput = ({
  value,
  maxDecimals,
  currency,
  onChange,
}: ValueInputProps & { currency: CurrencyCodeType }) => {
  const inputProps = useFormattedCurrencyInput({
    value,
    currency,
    onChange: (next) => onChange(next),
    maxDecimals,
    autoFocus: true,
    size: 'small',
    restFormatProps: { min: 0 },
  });

  return (
    <Input
      className="w-full text-right"
      aria-label="goal-value-currency"
      data-dd-action-name="Grid cell goal: edit currency value"
      {...inputProps}
    />
  );
};

const GoalValueInput = ({
  option,
  value,
  currency,
  onChange,
}: {
  option: GoalOption;
  value: number | null;
  currency?: CurrencyCodeType;
  onChange: (next: number | null) => void;
}) => {
  const maxDecimals = option.decimalPlaces ?? undefined;

  switch (option.valueType) {
    case 'currency': {
      if (!currency) {
        return null;
      }

      return (
        <CurrencyGoalInput
          value={value}
          maxDecimals={maxDecimals}
          currency={currency}
          onChange={onChange}
        />
      );
    }
    case 'percent':
      return (
        <PercentGoalInput
          value={value}
          maxDecimals={maxDecimals}
          onChange={onChange}
        />
      );
    case 'decimal':
      return (
        <NumberGoalInput
          value={value}
          maxDecimals={maxDecimals}
          onChange={onChange}
        />
      );
    default: {
      option.valueType satisfies never;
    }
  }
};

export const GoalEditor = ({
  groups = [],
  currency,
  title,
  hint,
  value: cellValue,
  onValueChange,
}: GoalEditorProps) => {
  const { t } = useTranslation('@mint-lib/ag-grid');

  const selectedCellId = cellValue?.id ?? null;

  const optionsById = useMemo(
    () =>
      new Map(
        groups.flatMap((group) =>
          group.options.map((option) => [option.value, option]),
        ),
      ),
    [groups],
  );

  const selectedOption = optionsById.get(selectedCellId ?? '');

  const [isListOpen, setIsListOpen] = useState(false);

  const handleSelectType = (option: GoalOption) => {
    onValueChange({ id: option.value, value: cellValue?.value ?? null });
    setIsListOpen(false);
  };

  const handleValueChange = (next: number | null) => {
    if (!selectedOption) return;
    onValueChange({ id: selectedOption.value, value: next });
  };

  const handleClear = () => {
    onValueChange(null);
  };

  return (
    <div className="flex w-75 translate-0 flex-col gap-2 bg-ui-primary p-3">
      <span className={typographyVariants({ variant: 'bodyShort01' })}>
        {title ?? t('Goal')}
      </span>

      <ComboboxInput
        size="small"
        value={selectedOption?.label ?? ''}
        placeholder={t('Select goal')}
        open={isListOpen}
        clearable
        onOpenChange={setIsListOpen}
        isPortal
        popoverClassName="z-popover ag-custom-component-popup"
        inputProps={{
          'aria-label': 'goal-type-trigger',
          'data-dd-action-name': 'Grid cell goal: toggle type',
          onChange: handleClear,
        }}
      >
        <ComboboxMenu withSearch emptyMessage={t('No options available')}>
          {groups.map((group) => (
            <ComboboxGroup key={group.key} header={group.label}>
              {group.options.map((option) => (
                <ComboboxItem
                  key={option.value}
                  value={`${group.label} ${option.label}`}
                  selected={option.value === selectedCellId}
                  onSelect={() => handleSelectType(option)}
                  data-dd-action-name="Grid cell goal: select type"
                >
                  <TruncateText tooltipClassName="break-all whitespace-normal">
                    {option.label}
                  </TruncateText>
                </ComboboxItem>
              ))}
            </ComboboxGroup>
          ))}
        </ComboboxMenu>
      </ComboboxInput>

      {selectedOption && !isListOpen && (
        <div className="flex flex-col gap-1">
          <GoalValueInput
            option={selectedOption}
            value={cellValue?.value ?? null}
            currency={currency}
            onChange={handleValueChange}
          />
          {hint && (
            <p
              className={typographyVariants({
                variant: 'caption1',
                color: 'secondary',
              })}
            >
              {hint}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
